import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRealtimeChannel } from "./useRealtimeChannel";
import { hintMatches } from "./hintFilter";

export const HINT_COALESCE_MS = 5_000;

const toKeys = (queryKey) => (Array.isArray(queryKey?.[0]) ? queryKey : [queryKey]);

/**
 * Invalidates queries when matching realtime hints arrive.
 *
 * The first hint invalidates immediately. Further hints within `windowMs`
 * are coalesced into a single invalidation when the window closes.
 *
 * `queryKey` is one react-query key or a list of keys.
 * `eventNames` ignores hints carrying none of these activity event names (see hintMatches).
 * `followUp` performs the closing invalidation even when no further hints arrive.
 */
export function useCoalescedInvalidation(
  channelPath,
  queryKey,
  { windowMs = HINT_COALESCE_MS, eventNames = null, followUp = false } = {},
) {
  const queryClient = useQueryClient();
  const keysRef = useRef(toKeys(queryKey));
  const eventNamesRef = useRef(eventNames);
  const timerRef = useRef(null);
  const pendingRef = useRef(false);

  keysRef.current = toKeys(queryKey);
  eventNamesRef.current = eventNames;

  useRealtimeChannel(channelPath, (msg) => {
    if (!hintMatches(msg, eventNamesRef.current)) return;

    if (timerRef.current) {
      pendingRef.current = true;
      return;
    }

    invalidate();
    pendingRef.current = followUp;
    timerRef.current = setTimeout(flush, windowMs);
  });

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  function invalidate() {
    for (const key of keysRef.current) {
      queryClient.invalidateQueries({ queryKey: key, cancelRefetch: false });
    }
  }

  function flush() {
    timerRef.current = null;

    if (!pendingRef.current) return;

    pendingRef.current = false;
    invalidate();
    timerRef.current = setTimeout(flush, windowMs);
  }
}
